import { FunctionComponent } from 'react';
import Slider from '@mui/material/Slider';
import {
  clampFpMagnitud,
  fpMagnitudSliderBounds,
  type TipoGdFp
} from '../../utils/factorPotenciaNormativo';

export type TipoMitigacionFila =
  | 'taps'
  | 'reactivos'
  | 'conductor'
  | 'transformador'
  | 'bateria'
  | 'combinacion'
  | 'reduccion_potencia';

export type TipoMedidaAtomica = Exclude<TipoMitigacionFila, 'combinacion'>;

export type ClasificacionMitigacion = 'operacional' | 'inversion';

export interface SubMedidaMitigacion {
  tipo: TipoMedidaAtomica;
  config: Record<string, unknown>;
}

export interface EscenarioMitigacion {
  tipoMitigacion: TipoMitigacionFila;
  clasificacion: ClasificacionMitigacion;
  parametrosJson: Record<string, unknown>;
}

const MAX_ESCENARIOS = 7;
/** Máximo de sub-medidas por fila combinación; cada tipo atómico solo una vez por combinación. */
export const MAX_SUBMEDIDAS_COMBINACION = 6;

const TIPOS_INVERSION = new Set<TipoMedidaAtomica>(['conductor', 'transformador', 'bateria']);

const FILA_TIPO_OPTIONS: { value: TipoMitigacionFila; label: string; clasificacion: ClasificacionMitigacion }[] = [
  { value: 'taps', label: 'Control de taps', clasificacion: 'operacional' },
  { value: 'reactivos', label: 'Control FP inversor', clasificacion: 'operacional' },
  { value: 'conductor', label: 'Cambio de conductor', clasificacion: 'inversion' },
  { value: 'transformador', label: 'Cambio de transformador', clasificacion: 'inversion' },
  { value: 'bateria', label: 'Batería', clasificacion: 'inversion' },
  {
    value: 'reduccion_potencia',
    label: 'Reducción de potencia de inyección',
    clasificacion: 'operacional'
  },
  { value: 'combinacion', label: 'Combinación', clasificacion: 'operacional' }
];

const SUB_TIPO_OPTIONS: { value: TipoMedidaAtomica; label: string }[] = [
  { value: 'taps', label: 'Control de taps' },
  { value: 'reactivos', label: 'Control FP inversor' },
  { value: 'conductor', label: 'Cambio de conductor' },
  { value: 'transformador', label: 'Cambio de transformador' },
  { value: 'bateria', label: 'Batería' },
  { value: 'reduccion_potencia', label: 'Reducción de potencia de inyección' }
];

function defaultPotenciaInyeccionKw(nominalKw?: number): number {
  if (nominalKw !== undefined && nominalKw > 1 && Number.isFinite(nominalKw)) {
    return Math.max(1, Math.round(nominalKw * 0.9 * 10) / 10);
  }
  return 100;
}

export function defaultConfigAtomic(
  tipo: TipoMedidaAtomica,
  potenciaNominalProyectoKw?: number
): Record<string, unknown> {
  switch (tipo) {
    case 'taps':
      return {
        tap_nosol: 1.0,
        tap_sol: 0.98,
        hora_inicio_sol: '08:00',
        hora_fin_sol: '19:00'
      };
    case 'reactivos':
      return {
        fp_sol: 0.95,
        fp_nosol: 1.0,
        tipo_fp_sol: 'inductivo',
        hora_inicio_sol: '08:00',
        hora_fin_sol: '19:00'
      };
    case 'conductor':
    case 'transformador':
      return {};
    case 'bateria':
      return {
        potencia_kw: 100,
        energia_kwh: 200,
        bloque_carga: '22:00-06:00',
        bloque_descarga: '10:00-16:00'
      };
    case 'reduccion_potencia':
      return {
        potencia_inyeccion_kw: defaultPotenciaInyeccionKw(potenciaNominalProyectoKw)
      };
    default:
      return {};
  }
}

export function emptyEscenario(): EscenarioMitigacion {
  return {
    tipoMitigacion: 'taps',
    clasificacion: 'operacional',
    parametrosJson: defaultConfigAtomic('taps')
  };
}

export function clasificacionCombinacionDesdeMedidas(medidas: SubMedidaMitigacion[]): ClasificacionMitigacion {
  return medidas.some((m) => TIPOS_INVERSION.has(m.tipo)) ? 'inversion' : 'operacional';
}

/** Payload `POST …/mitigaciones` alineado a FASE03 + Julia (`tipo` + `config` por sub-medida). */
export function medidasParaApi(escenarios: EscenarioMitigacion[]) {
  return escenarios.map((e, i) => {
    if (e.tipoMitigacion === 'combinacion') {
      const list = normalizarMedidasCombinacion(e.parametrosJson.medidas);
      return {
        tipoMitigacion: 'combinacion' as const,
        clasificacion: clasificacionCombinacionDesdeMedidas(list),
        parametrosJson: { medidas: list.map((m) => ({ tipo: m.tipo, config: m.config })) },
        orden: i
      };
    }
    return {
      tipoMitigacion: e.tipoMitigacion,
      clasificacion: e.clasificacion,
      parametrosJson: { ...e.parametrosJson },
      orden: i
    };
  });
}

function capMedidasTiposUnicos(medidas: SubMedidaMitigacion[]): SubMedidaMitigacion[] {
  const seen = new Set<TipoMedidaAtomica>();
  const out: SubMedidaMitigacion[] = [];
  for (const item of medidas) {
    if (!item.tipo || seen.has(item.tipo)) continue;
    seen.add(item.tipo);
    out.push(item);
    if (out.length >= MAX_SUBMEDIDAS_COMBINACION) break;
  }
  return out.length > 0 ? out : [{ tipo: 'taps', config: defaultConfigAtomic('taps') }];
}

function normalizarMedidasCombinacion(raw: unknown): SubMedidaMitigacion[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [{ tipo: 'taps', config: defaultConfigAtomic('taps') }];
  }
    const mapped = raw.map((item: any) => {
    const tipo = (item?.tipo as TipoMedidaAtomica) || 'taps';
    const cfg = item?.config;
    const config =
      cfg && typeof cfg === 'object' && !Array.isArray(cfg)
        ? { ...cfg }
        : defaultConfigAtomic(tipo, undefined);
    return { tipo, config };
  });
  return capMedidasTiposUnicos(mapped);
}

/** Convierte filas API → estado editor (GET mitigaciones). */
export function escenariosDesdeApi(rows: Record<string, unknown>[]): EscenarioMitigacion[] {
  return rows.map((r) => {
    const tipo = r.tipoMitigacion as TipoMitigacionFila;
    const pj = (r.parametrosJson as Record<string, unknown>) || {};
    if (tipo === 'combinacion') {
      return {
        tipoMitigacion: 'combinacion',
        clasificacion: (r.clasificacion as ClasificacionMitigacion) || 'operacional',
        parametrosJson: { medidas: normalizarMedidasCombinacion(pj.medidas) }
      };
    }
    return {
      tipoMitigacion: tipo,
      clasificacion: (r.clasificacion as ClasificacionMitigacion) || 'operacional',
      parametrosJson: { ...pj }
    };
  });
}

/** Muestra mín y máx junto al slider para que el rango normativo sea siempre explícito. */
function FpSlider({
  label,
  value,
  min,
  max,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className='ecg-form-field' style={{ maxWidth: 240 }}>
      <label className='ecg-label'>
        {label}: <strong>{value.toFixed(2)}</strong>
        <span className='vdr-ecg-label-range'>
          ({min.toFixed(2)} – {max.toFixed(2)})
        </span>
      </label>
      <Slider
        value={value}
        min={min}
        max={max}
        step={0.01}
        onChange={(_, v) => onChange(v as number)}
        sx={{ color: 'var(--vdr-cyan-dark)' }}
      />
    </div>
  );
}

/** Control FP inversor — rangos NORM-009; componente propio para que los hooks sean válidos. */
const ParametrosReactivosMitigacion: FunctionComponent<{
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  tipoGd: TipoGdFp;
}> = ({ config, onChange, tipoGd }) => {
  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });

  const tipoSol: 'inductivo' | 'capacitivo' =
    String(config.tipo_fp_sol ?? 'inductivo').toLowerCase().trim() === 'capacitivo'
      ? 'capacitivo'
      : 'inductivo';

  // Rango normativo para horario sol (depende de tipo instalación + naturaleza elegida)
  const boundsSol = fpMagnitudSliderBounds(tipoGd, tipoSol);
  // Magnitud mostrada: siempre dentro del rango normativo
  const fpSol = clampFpMagnitud(Number(config.fp_sol ?? boundsSol.min), tipoGd, tipoSol);

  // Horario no-sol: siempre inductivo efectivo, mín 0,95 (sin restricción capacitivo)
  const boundsNoSol = { min: 0.95, max: 1.0 };
  const fpNo = Math.min(1.0, Math.max(0.95, Number(config.fp_nosol ?? 1.0)));

  const handleNaturalezaChange = (nature: 'inductivo' | 'capacitivo') => {
    const newBounds = fpMagnitudSliderBounds(tipoGd, nature);
    // Ajusta fp_sol al mínimo del nuevo rango si quedó fuera
    const adj = clampFpMagnitud(Number(config.fp_sol ?? newBounds.min), tipoGd, nature);
    onChange({ ...config, tipo_fp_sol: nature, fp_sol: adj });
  };

  const hintNormativo =
    tipoGd === 'PMGD'
      ? 'PMGD (NTCO): inductivo ≥ 0,95; capacitivo ≥ 0,96 (Art. 3-19/3-31).'
      : 'EG (Netbilling): inductivo y capacitivo ≥ 0,95 (Art. 5-18/5-19).';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Naturaleza */}
      <div className='ecg-form-field' style={{ maxWidth: 220 }}>
        <label className='ecg-label'>Naturaleza FP (bloque)</label>
        <select
          className='ecg-input'
          value={tipoSol}
          onChange={(e) =>
            handleNaturalezaChange(e.target.value === 'capacitivo' ? 'capacitivo' : 'inductivo')
          }>
          <option value='inductivo'>Inductivo (atrasado)</option>
          <option value='capacitivo'>Capacitivo (adelantado)</option>
        </select>
        <p className='ecg-hint' style={{ marginTop: '0.3rem', fontSize: '12px' }}>
          {hintNormativo}
        </p>
      </div>

      {/* FP horario sol */}
      <FpSlider
        label='FP bloque'
        value={fpSol}
        min={boundsSol.min}
        max={boundsSol.max}
        onChange={(v) => set('fp_sol', v)}
      />

      {/* FP horario no-sol */}
      <FpSlider
        label='FP fuera bloque'
        value={fpNo}
        min={boundsNoSol.min}
        max={boundsNoSol.max}
        onChange={(v) => set('fp_nosol', v)}
      />

      {/* Ventanas horarias */}
      <div className='ecg-form-field' style={{ maxWidth: 130 }}>
        <label className='ecg-label'>Inicio bloque</label>
        <input
          type='text'
          className='ecg-input'
          value={String(config.hora_inicio_sol ?? '08:00')}
          onChange={(e) => set('hora_inicio_sol', e.target.value)}
        />
      </div>
      <div className='ecg-form-field' style={{ maxWidth: 130 }}>
        <label className='ecg-label'>Fin bloque</label>
        <input
          type='text'
          className='ecg-input'
          value={String(config.hora_fin_sol ?? '19:00')}
          onChange={(e) => set('hora_fin_sol', e.target.value)}
        />
      </div>
    </div>
  );
};

const ParametrosMedidaAtomica: FunctionComponent<{
  tipo: TipoMedidaAtomica;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  potenciaNominalProyectoKw?: number;
  /** Instalación del estudio base (NORM-009 — límites FP mitigación reactivos). */
  tipoGd?: TipoGdFp;
}> = ({ tipo, config, onChange, potenciaNominalProyectoKw, tipoGd = 'PMGD' }) => {
  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });

  if (tipo === 'taps') {
    return (
      <div className='ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 150 }}>
          <label className='ecg-label'>Tap dentro bloque</label>
          <input
            type='number'
            className='ecg-input'
            step='0.01'
            value={Number(config.tap_sol ?? 0.98)}
            onChange={(e) => set('tap_sol', Number(e.target.value))}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 150 }}>
          <label className='ecg-label'>Tap fuera bloque</label>
          <input
            type='number'
            className='ecg-input'
            step='0.01'
            value={Number(config.tap_nosol ?? 1)}
            onChange={(e) => set('tap_nosol', Number(e.target.value))}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 150 }}>
          <label className='ecg-label'>Inicio bloque</label>
          <input
            type='text'
            className='ecg-input'
            placeholder='08:00'
            value={String(config.hora_inicio_sol ?? '08:00')}
            onChange={(e) => set('hora_inicio_sol', e.target.value)}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 150 }}>
          <label className='ecg-label'>Fin bloque</label>
          <input
            type='text'
            className='ecg-input'
            placeholder='19:00'
            value={String(config.hora_fin_sol ?? '19:00')}
            onChange={(e) => set('hora_fin_sol', e.target.value)}
          />
        </div>
      </div>
    );
  }

  if (tipo === 'reactivos') {
    return <ParametrosReactivosMitigacion config={config} onChange={onChange} tipoGd={tipoGd} />;
  }

  if (tipo === 'conductor' || tipo === 'transformador') {
    return (
      <p className='ecg-hint' style={{ marginTop: '0.25rem' }}>
        Se aplicarán los refuerzos sugeridos del estudio base. No requiere parámetros adicionales.
      </p>
    );
  }

  if (tipo === 'reduccion_potencia') {
    const nom = potenciaNominalProyectoKw;
    return (
      <div className='ecg-form-row' style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <div className='ecg-form-field' style={{ maxWidth: 220 }}>
          <label className='ecg-label'>Potencia de inyección (kW)</label>
          <input
            type='number'
            className='ecg-input'
            step='0.1'
            value={
              config.potencia_inyeccion_kw === undefined || config.potencia_inyeccion_kw === ''
                ? ''
                : Number(config.potencia_inyeccion_kw)
            }
            onChange={(e) =>
              set('potencia_inyeccion_kw', e.target.value === '' ? '' : Number(e.target.value))
            }
          />
        </div>
        {nom !== undefined && Number.isFinite(nom) && nom > 0 && (
          <p className='ecg-hint' style={{ marginTop: '1.5rem', flex: '1 1 200px' }}>
            Debe ser mayor que 0 y menor que la potencia nominal del proyecto (<strong>{nom} kW</strong>).
          </p>
        )}
      </div>
    );
  }

  if (tipo === 'bateria') {
    return (
      <div className='ecg-battery-fields ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 160 }}>
          <label className='ecg-label'>Potencia (kW)</label>
          <input
            type='number'
            className='ecg-input'
            value={config.potencia_kw === undefined || config.potencia_kw === '' ? '' : Number(config.potencia_kw)}
            onChange={(e) => set('potencia_kw', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 160 }}>
          <label className='ecg-label'>Energía (kWh)</label>
          <input
            type='number'
            className='ecg-input'
            value={config.energia_kwh === undefined || config.energia_kwh === '' ? '' : Number(config.energia_kwh)}
            onChange={(e) => set('energia_kwh', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 160 }}>
          <label className='ecg-label'>Bloque carga</label>
          <input
            type='text'
            className='ecg-input'
            value={String(config.bloque_carga ?? '22:00-06:00')}
            onChange={(e) => set('bloque_carga', e.target.value)}
          />
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 160 }}>
          <label className='ecg-label'>Bloque descarga</label>
          <input
            type='text'
            className='ecg-input'
            value={String(config.bloque_descarga ?? '10:00-16:00')}
            onChange={(e) => set('bloque_descarga', e.target.value)}
          />
        </div>
      </div>
    );
  }

  return null;
};

export interface MitigacionEscenariosEditorProps {
  bateriaEnProyecto: boolean;
  /** Potencia nominal del proyecto (kW), para defaults y validación visual de reducción de inyección. */
  potenciaNominalProyectoKw?: number;
  /** PMGD vs EG del estudio base (NORM-009). Por defecto PMGD. */
  tipoGd?: TipoGdFp;
  value: EscenarioMitigacion[];
  onChange: (next: EscenarioMitigacion[]) => void;
}

const MitigacionEscenariosEditor: FunctionComponent<MitigacionEscenariosEditorProps> = ({
  bateriaEnProyecto,
  potenciaNominalProyectoKw,
  tipoGd = 'PMGD',
  value: escenarios,
  onChange
}) => {
  const opcionesFila = FILA_TIPO_OPTIONS.filter((o) => !bateriaEnProyecto || o.value !== 'bateria');
  const opcionesSub = SUB_TIPO_OPTIONS.filter((o) => !bateriaEnProyecto || o.value !== 'bateria');

  const opcionesSubParaIndice = (medidas: SubMedidaMitigacion[], subIdx: number) => {
    const usadosOtros = new Set<TipoMedidaAtomica>();
    medidas.forEach((row, k) => {
      if (k !== subIdx) usadosOtros.add(row.tipo);
    });
    return opcionesSub.filter((o) => o.value === medidas[subIdx]?.tipo || !usadosOtros.has(o.value));
  };

  const puedeAnadirSubmedida = (medidas: SubMedidaMitigacion[]) => {
    if (medidas.length >= MAX_SUBMEDIDAS_COMBINACION) return false;
    const usados = new Set(medidas.map((m) => m.tipo));
    return opcionesSub.some((o) => !usados.has(o.value));
  };

  const patch = (idx: number, partial: Partial<EscenarioMitigacion>) => {
    onChange(escenarios.map((e, i) => (i === idx ? { ...e, ...partial } : e)));
  };

  const updateTipoFila = (idx: number, tipo: TipoMitigacionFila) => {
    const opt = FILA_TIPO_OPTIONS.find((o) => o.value === tipo);
    if (tipo === 'combinacion') {
      patch(idx, {
        tipoMitigacion: 'combinacion',
        clasificacion: 'operacional',
        parametrosJson: {
          medidas: [{ tipo: 'taps', config: defaultConfigAtomic('taps') }]
        }
      });
      return;
    }
    patch(idx, {
      tipoMitigacion: tipo,
      clasificacion: opt?.clasificacion || 'operacional',
      parametrosJson: defaultConfigAtomic(tipo as TipoMedidaAtomica, potenciaNominalProyectoKw)
    });
  };

  const medidasDeCombinacion = (idx: number): SubMedidaMitigacion[] => {
    const e = escenarios[idx];
    const raw = e.parametrosJson.medidas;
    return normalizarMedidasCombinacion(raw);
  };

  const setMedidasCombinacion = (idx: number, medidas: SubMedidaMitigacion[]) => {
    const cls = clasificacionCombinacionDesdeMedidas(medidas);
    patch(idx, {
      clasificacion: cls,
      parametrosJson: { medidas }
    });
  };

  const updateSubTipo = (idx: number, subIdx: number, tipo: TipoMedidaAtomica) => {
    const m = [...medidasDeCombinacion(idx)];
    m[subIdx] = { tipo, config: defaultConfigAtomic(tipo, potenciaNominalProyectoKw) };
    setMedidasCombinacion(idx, m);
  };

  const updateSubConfig = (idx: number, subIdx: number, config: Record<string, unknown>) => {
    const m = [...medidasDeCombinacion(idx)];
    m[subIdx] = { ...m[subIdx], config };
    setMedidasCombinacion(idx, m);
  };

  const addSub = (idx: number) => {
    const m = medidasDeCombinacion(idx);
    if (!puedeAnadirSubmedida(m)) return;
    const usados = new Set(m.map((x) => x.tipo));
    const siguiente = opcionesSub.find((o) => !usados.has(o.value));
    if (!siguiente) return;
    setMedidasCombinacion(idx, [
      ...m,
      { tipo: siguiente.value, config: defaultConfigAtomic(siguiente.value, potenciaNominalProyectoKw) }
    ]);
  };

  const removeSub = (idx: number, subIdx: number) => {
    const m = medidasDeCombinacion(idx);
    if (m.length <= 1) return;
    setMedidasCombinacion(
      idx,
      m.filter((_, i) => i !== subIdx)
    );
  };

  const addEscenario = () => {
    if (escenarios.length >= MAX_ESCENARIOS) return;
    onChange([...escenarios, emptyEscenario()]);
  };

  const removeEscenario = (idx: number) => {
    if (escenarios.length <= 1) return;
    onChange(escenarios.filter((_, i) => i !== idx));
  };

  return (
    <div className='ecg-section'>
      <h3 className='ecg-section-title'>Escenarios de mitigación ({escenarios.length}/{MAX_ESCENARIOS})</h3>
      <p className='vdr-ecg-lead'>
        Cada fila es un escenario evaluado por el motor. En <strong>Combinación</strong> puede apilar medidas atómicas
        distintas; el orden es el de la lista. Máximo <strong>{MAX_SUBMEDIDAS_COMBINACION}</strong> sub-medidas y{' '}
        <strong>cada tipo</strong> (taps, reactivos, conductor, transformador, batería, reducción de potencia){' '}
        <strong>solo una vez</strong> por combinación.
      </p>
      {bateriaEnProyecto && (
        <p className='ecg-hint' style={{ marginBottom: '1rem', color: 'var(--vdr-warning)' }}>
          Este proyecto ya incluye <strong>batería</strong> en el diseño: no puede añadirse la medida mitigación
          &quot;Batería&quot; (ni dentro de una combinación).
        </p>
      )}

      {escenarios.map((esc, idx) => {
        const cls = esc.tipoMitigacion === 'combinacion' ? clasificacionCombinacionDesdeMedidas(medidasDeCombinacion(idx)) : esc.clasificacion;
        return (
          <div key={idx} className='ecg-mitigacion-row'>
            <div className='ecg-mitigacion-header'>
              <div className='vdr-ecg-row-head'>
                <strong>Escenario {idx + 1}</strong>
                <span className='ecg-mit-escenario-modo'>
                  {esc.tipoMitigacion === 'combinacion' ? 'Combinación' : 'Individual'}
                </span>
                {esc.tipoMitigacion === 'combinacion' && medidasDeCombinacion(idx).length > 0 && (
                  <span className='ecg-mit-sub-chips'>
                    {medidasDeCombinacion(idx).map((sm, j) => (
                      <span key={j} className='ecg-mit-sub-chip'>
                        {SUB_TIPO_OPTIONS.find((o) => o.value === sm.tipo)?.label ?? sm.tipo}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              {escenarios.length > 1 && (
                <button
                  type='button'
                  className='vdr-ecg-link-danger'
                  onClick={() => removeEscenario(idx)}>
                  Eliminar escenario
                </button>
              )}
            </div>

            <div className='ecg-form-row'>
              <div className='ecg-form-field' style={{ maxWidth: 300 }}>
                <label className='ecg-label'>Tipo de escenario</label>
                <select
                  className='ecg-input'
                  value={esc.tipoMitigacion}
                  onChange={(e) => updateTipoFila(idx, e.target.value as TipoMitigacionFila)}>
                  {opcionesFila.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Clasificación (costeo)</label>
                <span className={`ecg-clasificacion-chip ${cls}`}>{cls}</span>
              </div>
            </div>

            {esc.tipoMitigacion === 'combinacion' ? (
              <div className='ecg-mit-combinacion-stack'>
                {medidasDeCombinacion(idx).map((sm, subIdx) => (
                  <div key={subIdx} className='ecg-mit-submedida-card'>
                    <div className='ecg-mit-submedida-head'>
                      <span className='ecg-label'>Sub-medida {subIdx + 1}</span>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select
                          className='ecg-input'
                          style={{ maxWidth: 220 }}
                          value={sm.tipo}
                          onChange={(e) => updateSubTipo(idx, subIdx, e.target.value as TipoMedidaAtomica)}>
                          {opcionesSubParaIndice(medidasDeCombinacion(idx), subIdx).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        {medidasDeCombinacion(idx).length > 1 && (
                          <button
                            type='button'
                            className='ecg-mit-sub-remove'
                            onClick={() => removeSub(idx, subIdx)}>
                            Quitar
                          </button>
                        )}
                      </div>
                    </div>
                    <ParametrosMedidaAtomica
                      tipo={sm.tipo}
                      config={sm.config}
                      onChange={(c) => updateSubConfig(idx, subIdx, c)}
                      potenciaNominalProyectoKw={potenciaNominalProyectoKw}
                      tipoGd={tipoGd}
                    />
                  </div>
                ))}
                {puedeAnadirSubmedida(medidasDeCombinacion(idx)) && (
                  <button type='button' className='ecg-mit-add-sub' onClick={() => addSub(idx)}>
                    + Añadir medida a la combinación
                  </button>
                )}
              </div>
            ) : (
              <ParametrosMedidaAtomica
                tipo={esc.tipoMitigacion as TipoMedidaAtomica}
                config={esc.parametrosJson}
                onChange={(c) => patch(idx, { parametrosJson: c })}
                potenciaNominalProyectoKw={potenciaNominalProyectoKw}
                tipoGd={tipoGd}
              />
            )}
          </div>
        );
      })}

      {escenarios.length < MAX_ESCENARIOS && (
        <button type='button' className='ecg-mit-add-escenario' onClick={addEscenario}>
          + Agregar escenario
        </button>
      )}
    </div>
  );
};

export default MitigacionEscenariosEditor;
export { MAX_ESCENARIOS };
